import 'react-toastify/dist/ReactToastify.css';
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import moment from "moment";
import Pagination from "react-js-pagination";
import Select from "react-select";
import ReactDatetime from "react-datetime";
import {
  faInfo,
  faTrash,
  faTimes,
  faTrashRestoreAlt,
  faPencilAlt,
  faCheckCircle,
  faCopy,
} from "@fortawesome/free-solid-svg-icons";
import {
  Row,
  Col,
  Card,
  CardHeader,
  CardTitle,
  Button,
  CardBody,
  Table,
  ModalBody,
  Modal,
  Input,
  Form,
  Spinner,
  Tooltip,
} from "reactstrap";
import ReactBSAlert from "react-bootstrap-sweetalert";
import widgetsService from "../../services/api/football-widget-service";
import generalHelper from "../../variables/general-helper.js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toast,ToastContainer } from "react-toastify";

const FootballWidget = () => {
  const history = useHistory();
  const [modalOpen, setModalOpen] = useState(false);
  const [detailedWidget, setDetailedWidget] = useState({});
  const [widgets, setWidgets] = useState([]);
  const [totalWidgets, setTotalWidgets] = useState(0);
  const [activePage, setActivePage] = useState(1);
  const [alert, setAlert] = useState("");
  const [activeTooltipId, setActiveTooltipId] = useState(null);
  const [copiedMap, setCopiedMap] = useState({});

  // These will now hold the pre-formatted { label, value } options from our new API
  const [tournamentOptions, setTournamentOptions] = useState([]);
  const [allMatchOptions, setAllMatchOptions] = useState([]);
  const [filteredMatchOptions, setFilteredMatchOptions] = useState([]);

  const [renderComponent, setRenderComponent] = useState(false);
  const records = 10;

  const initialFormState = {
    widget_name: "",
    client_name: "",
    template: "",
    layout: "",
    from_date: "",
    to_date: "",
    tournament_slugs: [],
    match_slugs: [],
    widget_code: "",
  };

  const LAYOUT = [
    { label: "Listing", value: "listing" },
    { label: "Carousel", value: "carousel" },
  ];
  const TEMPLATE = [
    { label: "Tournament Carousel", value: "tournament" },
    { label: "Single Match Card", value: "single-match" },
    { label: "Full Match Detail Page", value: "full-match-detail" },
    { label: "Points Table", value: "tournament-points-table" },
    { label: "Recent Tournaments List", value: "recent-tournaments-list" },
  ];
  const [formState, setFormState] = useState(initialFormState);

  const paginationData = useMemo(() => {
    const firstRecord = (activePage - 1) * records + 1;
    const lastRecord = Math.min(activePage * records, totalWidgets);
    return { firstRecord, lastRecord };
  }, [activePage, totalWidgets]);

  const today = moment().startOf('day');

  const fetchWidgets = useCallback(async () => {
    try {
      setRenderComponent(true);
      
      const apiFilters = { ...formState };
      
      // The complex 'if/else' block for template and match_slugs size is now removed.
      // We will now pass the template name directly as selected in the form.

      // This loop correctly removes any empty or null values before sending to the API.
      const finalFilters = {};
      for (const key in apiFilters) {
        const value = apiFilters[key];
        if (value !== '' && value !== null && (!Array.isArray(value) || value.length > 0)) {
          finalFilters[key] = value;
        }
      }

      const widgetsData = await widgetsService.getFootballWidgetList(
        finalFilters,
        activePage,
        records
      );

      if (widgetsData.status && widgetsData.data) {
        setWidgets(widgetsData.data.footballWidgets);
        setTotalWidgets(widgetsData.data.count);
      }
    } catch (error) {
      console.error("Error fetching widgets:", error);
      toast.error("Failed to load widgets. Please try again.");
    } finally {
        setRenderComponent(false);
    }
  }, [formState, activePage]);

  const toggleTooltip = (id) => {
    setActiveTooltipId(id === activeTooltipId ? null : id);
  };
  
  const handlePageChange = useCallback((pageNumber) => {
    generalHelper.addKeyToQueryString("page", pageNumber);
    setActivePage(pageNumber);
  }, []);

  const handleModalOpen = useCallback((widget) => {
    setDetailedWidget(widget);
    setModalOpen(true);
  }, []);

  const handleCopy = useCallback((widgetId, codeToCopy) => {
    navigator.clipboard.writeText(codeToCopy);
    setCopiedMap((prev) => ({ ...prev, [widgetId]: true }));
    setTimeout(() => {
      setCopiedMap((prev) => ({ ...prev, [widgetId]: false }));
    }, 2000);
  }, []);

  const handleEmbedOpen = useCallback((widget) => {
    handleCopy(widget._id, widget.js_embed);
  }, [handleCopy]);

  const handleEdit = useCallback(
    (id) => {
      history.push(`/dashboard/football-widget/edit/${id}`);
    },
    [history]
  );

  const handleFormChange = useCallback((field, value) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleFilterReset = useCallback(() => {
    setFormState(initialFormState);
    setActivePage(1);
  }, [initialFormState]);

  const handleFilterSubmit = useCallback(
    (e) => {
      e.preventDefault();
      setActivePage(1);
      fetchWidgets();
    },
    [fetchWidgets]
  );
  
  const confirmAction = useCallback((widget) => {
    const action = widget.status ? "delete" : "restore";

    setAlert(
      <ReactBSAlert
        warning
        style={{ display: "block", marginTop: "-100px" }}
        title={`Are you sure you want to ${action} this widget?`}
        onConfirm={() => handleWidgetAction(widget._id, action)}
        onCancel={() => setAlert(null)}
        confirmBtnBsStyle="info"
        cancelBtnBsStyle="danger"
        confirmBtnText={`Yes, ${action} it!`}
        cancelBtnText="Back"
        showCancel
      >
        <p>This action will {action} the selected widget.</p>
        <hr />
        <p>
          <strong>Widget Name:</strong> {widget.widget_name || "N/A"}
        </p>
        <p>
          <strong>Client Name:</strong> {widget.client_name || "N/A"}
        </p>
      </ReactBSAlert>
    );
  }, []);

  const handleWidgetAction = useCallback(
    async (id, action) => {
      setAlert(null);
      try {
        setAlert(
          <ReactBSAlert
            info
            style={{ display: "block", marginTop: "-100px" }}
            title={`${action === "delete" ? "Deleting" : "Restoring"} widget...`}
            showConfirm={false}
          />
        );

        const res = await widgetsService.deleteWidget({ id: id });

        if (res && res.status) {
          setAlert(
            <ReactBSAlert
              success
              style={{ display: "block", marginTop: "-100px" }}
              title={`Widget ${action === "delete" ? "deleted" : "restored"} successfully`}
              onConfirm={() => setAlert(null)}
            />
          );
        } else {
          setAlert(
            <ReactBSAlert
              danger
              style={{ display: "block", marginTop: "-100px" }}
              title={"Something went wrong. Please try again."}
              onConfirm={() => setAlert(null)}
            />
          );
        }

        fetchWidgets();
      } catch (error) {
        console.error(`Error ${action}ing widget:`, error);
        setAlert(
          <ReactBSAlert
            danger
            style={{ display: "block", marginTop: "-100px" }}
            title={`Failed to ${action} widget. Please try again.`}
            onConfirm={() => setAlert(null)}
          />
        );
      }
    },
    [fetchWidgets]
  );

  useEffect(() => {
    const fetchDataForFilters = async () => {
        try {
            const res = await widgetsService.getActiveFilters();
            if (res.status && res.data) {
                setTournamentOptions(res.data.tournamentOptions || []);
                setAllMatchOptions(res.data.matchOptions || []);
                setFilteredMatchOptions(res.data.matchOptions || []); // Initially, show all matches
            } else {
                toast.error("Could not load filter options.");
            }
        } catch (err) {
            console.error("Error fetching data for filters:", err);
            toast.error("Error fetching filter data!");
        }
    };

    fetchDataForFilters();
  }, []);

  useEffect(() => {
      const selectedTournamentSlugs = formState.tournament_slugs;

      if (selectedTournamentSlugs && selectedTournamentSlugs.length > 0) {
          const relevantMatches = allMatchOptions.filter(match => 
              match.tournament_slug && selectedTournamentSlugs.includes(match.tournament_slug)
          );
          setFilteredMatchOptions(relevantMatches);
          
          const validSelectedMatchSlugs = formState.match_slugs.filter(slug =>
              relevantMatches.some(m => m.value === slug)
          );
          if (validSelectedMatchSlugs.length !== formState.match_slugs.length) {
              handleFormChange('match_slugs', validSelectedMatchSlugs);
          }

      } else {
          // If no tournament is selected, show all available matches
          setFilteredMatchOptions(allMatchOptions);
      }
  }, [formState.tournament_slugs, allMatchOptions, handleFormChange, formState.match_slugs]);

  useEffect(() => {
    fetchWidgets();
  }, [activePage, fetchWidgets]);

  return (
    <div className="content">
    <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        pauseOnHover
      />
      {alert}
      <Row>
        <Col className="ml-auto mr-auto" md="12">
          <Card className="card-signup text-center">
            <CardHeader>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <CardTitle tag="h4">Football Widget</CardTitle>
                <div>
                  <Button
                    className="btn-round"
                    color="danger"
                    size="sm"
                    type="button"
                    onClick={() =>
                      history.push("/dashboard/football-widget/create")
                    }
                  >
                    Create Widget
                  </Button>
                </div>
              </div>

              <Form onSubmit={handleFilterSubmit}>
                <Row>
                  <Col md="3">
                    <Input type="text" placeholder="Widget Name" value={formState.widget_name} onChange={(e) => handleFormChange("widget_name", e.target.value)} />
                  </Col>
                  <Col md="3">
                    <Input type="text" placeholder="Client Name" value={formState.client_name} onChange={(e) => handleFormChange("client_name", e.target.value)} />
                  </Col>
                  <Col md="3">
                    <Select isClearable placeholder="Select Template" options={TEMPLATE} value={TEMPLATE.find((option) => option.value === formState.template) || null} onChange={(selectedOption) => handleFormChange("template", selectedOption ? selectedOption.value : "")} />
                  </Col>
                  <Col md="3">
                    <Select
                      isMulti
                      options={tournamentOptions}
                      value={tournamentOptions.filter((option) => formState.tournament_slugs.includes(option.value))}
                      onChange={(selected) => handleFormChange("tournament_slugs", selected ? selected.map((opt) => opt.value) : [])}
                      placeholder="Select Tournaments"
                    />
                  </Col>
                </Row>
                <Row className="mt-3">
                  <Col md="3">
                    <Select
                      isMulti
                      placeholder="Select Matches"
                      options={filteredMatchOptions}
                      value={filteredMatchOptions.filter((option) => formState.match_slugs.includes(option.value))}
                      onChange={(selected) => handleFormChange("match_slugs", selected ? selected.map((opt) => opt.value) : [])}
                    />
                  </Col>
                  <Col md="3">
                    <ReactDatetime
                      value={formState.from_date || null}
                      onChange={(date) => handleFormChange("from_date", date._d)}
                      renderDay={(props, currentDate) => {
                        const isToday = currentDate.isSame(today, 'day');
                        const style = isToday ? { backgroundColor: '#ffefc3', borderRadius: '50%', fontWeight: 'bold', color: '#333' } : {};
                        return <td {...props} style={style}>{currentDate.date()}</td>;
                      }}
                      inputProps={{ className: "form-control", placeholder: "From Date" }}
                    />
                  </Col>
                  <Col md="3">
                    <ReactDatetime
                      value={formState.to_date || null}
                      onChange={(date) => handleFormChange("to_date", date._d)}
                      renderDay={(props, currentDate) => {
                        const isToday = currentDate.isSame(today, 'day');
                        const style = isToday ? { backgroundColor: '#ffefc3', borderRadius: '50%', fontWeight: 'bold', color: '#333' } : {};
                        return <td {...props} style={style}>{currentDate.date()}</td>;
                      }}
                      inputProps={{ className: "form-control", placeholder: "To Date" }}
                    />
                  </Col>
                  <Col md="3">
                    <Select isClearable placeholder="Select Layout" options={LAYOUT} value={LAYOUT.find((option) => option.value === formState.layout) || null} onChange={(selectedOption) => handleFormChange("layout", selectedOption ? selectedOption.value : "")} />
                  </Col>
                </Row>
                <Row className="mt-3">
                  <Col md="3">
                    <Input type="text" placeholder="Widget Code" value={formState.widget_code} onChange={(e) => handleFormChange("widget_code", e.target.value)} />
                  </Col>
                  <Col md="3">
                    <Button className="btn-round mr-2" color="info" type="submit">Search</Button>
                    <Button className="btn-round" color="warning" type="button" onClick={handleFilterReset}>Reset</Button>
                  </Col>
                </Row>
              </Form>
              <hr />
              {totalWidgets > 0 && (
                <Row>
                  <Col md="6">
                    <div style={{ float: "right", margin: "5px" }}><b>{paginationData.firstRecord} - {paginationData.lastRecord} of {totalWidgets}</b></div>
                  </Col>
                  <Col md="6">
                    <div style={{ float: "left" }}>
                      <Pagination activePage={activePage} itemsCountPerPage={records} totalItemsCount={totalWidgets} pageRangeDisplayed={3} onChange={handlePageChange} itemClass="page-item" linkClass="page-link" />
                    </div>
                  </Col>
                </Row>
              )}
            </CardHeader>
            <CardBody>
              <Table responsive>
                <thead className="text-primary">
                  <tr>
                    <th className="text-left">S.no</th>
                    <th className="text-left">Name</th>
                    <th className="text-left">Client</th>
                    <th className="text-left">Title</th>
                    <th className="text-left">Layout</th>
                    <th className="text-left">Template</th>
                    <th className="text-left">Updated At</th>
                    <th className="text-left">Created At</th>
                    <th className="text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {widgets.length > 0 ? (
                    widgets.map((widget, index) => (
                      <tr key={widget._id || index}>
                        <td className="text-left">{(activePage - 1) * records + index + 1}</td>
                        <td className="text-left">{widget.widget_name || ""}</td>
                        <td className="text-left">{widget.client_name || "NA"}</td>
                        <td className="text-left">{widget.title || ""}</td>
                        <td className="text-left">{widget.layout}</td>
                        <td className="text-left">{widget.template}</td>
                        <td className="text-left">{moment(widget.updatedAt).isValid() ? moment(widget.updatedAt).format("MMMM D, YYYY h:mm A") : ""}</td>
                        <td className="text-left">{moment(widget.createdAt).isValid() ? moment(widget.createdAt).format("MMMM D, YYYY h:mm A") : ""}</td>
                        <td className="text-center py-2 px-3">
                          <div className="d-flex align-items-center justify-content-center gap-2">
                            {widget.status ? (<Button color="danger" size="sm" className="btn-icon-only rounded-circle" onClick={() => confirmAction(widget)} title="Delete" style={{ padding: '0.4rem', width: '2rem', height: '2rem' }}><FontAwesomeIcon icon={faTrash} /></Button>) : (<Button color="success" size="sm" className="btn-icon-only rounded-circle" onClick={() => confirmAction(widget)} title="Restore" style={{ padding: '0.4rem', width: '2rem', height: '2rem' }}><FontAwesomeIcon icon={faTrashRestoreAlt} /></Button>)}
                            <Button color="info" size="sm" className="btn-icon-only rounded-circle" id={`copyEmbedBtn_${widget._id}`} onClick={() => handleEmbedOpen(widget)} title="Copy" style={{ padding: '0.4rem', width: '2rem', height: '2rem' }}><FontAwesomeIcon icon={copiedMap[widget._id] ? faCheckCircle : faCopy} /><Tooltip placement="top" isOpen={activeTooltipId === widget._id} target={`copyEmbedBtn_${widget._id}`} toggle={() => toggleTooltip(widget._id)}>{copiedMap[widget._id] ? "Copied!" : "Copy"}</Tooltip></Button>
                            <Button color="secondary" size="sm" className="btn-icon-only rounded-circle" onClick={() => handleModalOpen(widget)} title="Detailed View" style={{ padding: '0.4rem', width: '2rem', height: '2rem' }}><FontAwesomeIcon icon={faInfo} /></Button>
                            <Button color="primary" size="sm" className="btn-icon-only rounded-circle" onClick={() => handleEdit(widget._id)} title="Edit" style={{ padding: '0.4rem', width: '2rem', height: '2rem' }}><FontAwesomeIcon icon={faPencilAlt} /></Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : renderComponent ? (
                    <tr><td colSpan="9" className="text-center"><Spinner animation="border" role="status"><span className="sr-only">Loading...</span></Spinner></td></tr>
                  ) : (
                    <tr><td colSpan="9" className="text-center"><CardTitle tag="h4">No Records Found</CardTitle></td></tr>
                  )}
                </tbody>
              </Table>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} size="md">
        <ModalBody className="p-3 position-relative">
          <div className="position-absolute d-flex align-items-center justify-content-center" style={{ right: "12px", top: "8px", cursor: "pointer", zIndex: 1, backgroundColor: "red", borderRadius: "50%", width: "25px", height: "25px" }} onClick={() => setModalOpen(false)}>
            <FontAwesomeIcon icon={faTimes} className="text-white" />
          </div>
          <div className="mb-3">
            <h6 className="mb-2">Football Widget Details</h6>
            <div className="border rounded">
              {[
                { label: "Widget Name", value: detailedWidget?.widget_name },
                { label: "Widget Slug", value: detailedWidget?.widget_slug },
                { label: "Publisher Name", value: detailedWidget?.client_name },
                { label: "Publisher Slug", value: detailedWidget?.client_slug },
                { label: "Tournament Slug", value: detailedWidget?.tournament_slug },
                { label: "Round Slug", value: detailedWidget?.round_slug },
                { label: "Match Slug", value: detailedWidget?.match_slug },
                { label: "Title", value: detailedWidget?.title },
                { label: "Template", value: detailedWidget?.template },
                { label: "Layout", value: detailedWidget?.layout },
                { label: "Status", value: detailedWidget?.status ? "Active" : "Inactive" },
                { label: "JS Embed", value: detailedWidget?.js_embed },
                { label: "Widget Code", value: detailedWidget?.widget_code },
                { label: "Created At", value: moment(detailedWidget?.createdAt).isValid() ? moment(detailedWidget?.createdAt).format("MMMM D, YYYY h:mm A") : "" },
                { label: "Updated At", value: moment(detailedWidget?.updatedAt).isValid() ? moment(detailedWidget?.updatedAt).format("MMMM D, YYYY h:mm A") : "" },
              ].map((item, index) => (
                <Row key={index} className={`g-0 ${index > 0 ? "border-top" : ""}`}>
                  <Col xs="5" className="p-2 bg-light"><div className="fw-semibold text-dark">{item.label}</div></Col>
                  <Col className="p-2"><div className="fw-semibold" style={{whiteSpace: 'pre-wrap', wordBreak: 'break-all'}}>{item.value || ""}</div></Col>
                </Row>
              ))}
            </div>
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
};

export default FootballWidget;